import { RadioGroup } from '@/components/RadioGroup';
import { RevealSlot } from '@/components/RevealSlot';
import {
    EBorrowerCitizenshipStatus,
    selectResidencyStatus,
    setResidencyStatus,
} from '@/context/borrower';
import { selectIsNZ } from '@/context/display';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { useAppSelector as useSelectorNZ } from '@/context/storeHooks';
import { useFieldCopy } from '@/copy';

import { VisaExpiryDate } from '../VisaExpiryDate';
import { VisaNumber } from '../VisaNumber';

export interface IResidencyStatus {
    baseId: string;
    showErrors: boolean;
}

export function ResidencyStatus({
    baseId,
    showErrors,
}: IResidencyStatus): JSX.Element {
    // ***** Redux *****
    const fields = useFieldCopy();
    const residencyStatus = useAppSelector(selectResidencyStatus);
    const dispatch = useAppDispatch();

    // ***** Event Handlers *****
    function handleChange(value: string | boolean | undefined): void {
        dispatch(setResidencyStatus(value as EBorrowerCitizenshipStatus));
        if (value === EBorrowerCitizenshipStatus.VISA_HOLDER) return;
    }

    // ***** Render *****
    const isNZ = useSelectorNZ(selectIsNZ);
    const options = [
        {
            label: fields.residencyStatusOptions.citizen,
            value: isNZ
                ? EBorrowerCitizenshipStatus.NEWZETLAND_CITIZEN
                : EBorrowerCitizenshipStatus.AUSTRALIAN_CITIZEN,
            id: `${baseId}-button-residencyStatus-citizen`,
        },
        {
            label: isNZ
                ? 'PR'
                : fields.residencyStatusOptions.permanentResident,
            value: EBorrowerCitizenshipStatus.PERMANENT_RESIDENT,
            id: `${baseId}-button-residencyStatus-permanentResident`,
        },
        {
            label: fields.residencyStatusOptions.visa,
            value: EBorrowerCitizenshipStatus.VISA_HOLDER,
            id: `${baseId}-button-residencyStatus-visaHolder`,
        },
    ];
    return (
        <>
            <div data-testid='residencyStatus'>
                <RadioGroup
                    title='Residency status'
                    showErrors={showErrors}
                    onChange={handleChange}
                    options={options}
                    value={residencyStatus}
                    tooltipContent='Your visa or citizenship status, not the country you live in.'
                />
            </div>
            {/* isNZ never changes at runtime, so it stays a hard gate; only
                the answer-driven part animates. */}
            {!isNZ && (
                <RevealSlot
                    open={
                        residencyStatus ===
                        EBorrowerCitizenshipStatus.VISA_HOLDER
                    }
                >
                    <div className='_flex items-start space-x-2'>
                        <div style={{ flex: 1 }}>
                            <VisaNumber
                                showErrors={
                                    showErrors &&
                                    residencyStatus ===
                                        EBorrowerCitizenshipStatus.VISA_HOLDER
                                }
                                baseId={baseId}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <VisaExpiryDate
                                showErrors={
                                    showErrors &&
                                    residencyStatus ===
                                        EBorrowerCitizenshipStatus.VISA_HOLDER
                                }
                                baseId={baseId}
                            />
                        </div>
                    </div>
                </RevealSlot>
            )}
        </>
    );
}
